import React from 'react';
import { motion } from 'framer-motion';

const HotspotGuide: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-lg p-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          🎯 How to Fix Hotspot Positioning
        </h1>
        
        <div className="space-y-6">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <h2 className="text-xl font-semibold text-blue-900 mb-2">
              The Problem
            </h2>
            <p className="text-blue-800">
              Your hotspots aren't pointing to the exact areas on the image because the coordinates 
              were set manually and don't match the actual features in your waterarmor.jpg image.
            </p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
            <h2 className="text-xl font-semibold text-green-900 mb-2">
              The Solution
            </h2>
            <p className="text-green-800 mb-3">
              Use the interactive coordinate finder tool to visually identify the exact positions 
              of features on your image.
            </p>
            <ol className="text-green-800 space-y-2">
              <li>1. Go to the "🎯 Coordinate Finder" page</li>
              <li>2. Click and drag on the image to create hotspots</li>
              <li>3. Copy the generated coordinates</li>
              <li>4. Update your houseIllustrations.ts file</li>
            </ol>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
            <h2 className="text-xl font-semibold text-yellow-900 mb-2">
              Quick Steps
            </h2>
            <div className="text-yellow-800 space-y-3">
              <div className="flex items-start gap-3">
                <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-sm font-semibold">1</span>
                <div>
                  <strong>Navigate to Coordinate Finder:</strong> Click "🎯 Coordinate Finder" in the navigation menu
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-sm font-semibold">2</span>
                <div>
                  <strong>Create Hotspots:</strong> Click and drag on the image to create hotspots for each feature (pool, balcony, etc.)
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-sm font-semibold">3</span>
                <div>
                  <strong>Copy Coordinates:</strong> Click "Show Code" to get the exact coordinates
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-sm font-semibold">4</span>
                <div>
                  <strong>Update Data:</strong> Replace the hotspots in your houseIllustrations.ts file with the new coordinates
                </div>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border-l-4 border-purple-400 p-4 rounded">
            <h2 className="text-xl font-semibold text-purple-900 mb-2">
              Example Usage
            </h2>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
              <pre className="text-sm">
{`// Before (incorrect coordinates)
{
  id: 'pool-area',
  x: 25,  // This doesn't match the actual pool position
  y: 65,
  title: 'Swimming Pool',
  // ...
}

// After (correct coordinates from coordinate finder)
{
  id: 'pool-area',
  x: 35.2,  // Now matches the actual pool position
  y: 68.1,
  title: 'Swimming Pool',
  // ...
}`}
              </pre>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">💡 Pro Tips:</h3>
            <ul className="text-gray-700 space-y-1 text-sm">
              <li>• Use the grid overlay to get precise coordinates</li>
              <li>• Test on different screen sizes to ensure responsiveness</li>
              <li>• Make hotspots slightly larger than the actual feature for easier clicking</li>
              <li>• Use descriptive labels for each hotspot</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default HotspotGuide;
